<template>
  <div class="modal fade" id="uploadDocumentModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <!-- Header -->
        <div class="modal-header">
          <h4 class="modal-title">Upload Dokumen Permohonan</h4>

          <button
            @click="closeModal"
            type="button"
            class="close"
            data-dismiss="modal"
          >
            <span>&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <!-- SURAT PERMOHONAN -->
          <component-guard :requiredPermissions="['29.2']">
            <div class="document-section">
              <h6 class="section-title">Surat Permohonan Dari Klien</h6>

              <p class="section-description">
                Upload surat permohonan yang telah ditandatangani atau email
                permohonan dari klien.
              </p>

              <div
                v-for="(file, index) in suratPermohonanFiles"
                :key="'permohonan-' + index"
                class="uploaded-file"
              >
                <div>
                  <i class="far fa-check-circle mr-2"></i>
                  {{ file.name }}
                </div>

                <div @click="removePermohonan(index)">
                  <i class="fas fa-times remove-file"></i>
                </div>
              </div>

              <label
                class="upload-box"
                :class="[
                  {
                    'upload-box-error': fieldErrors.includes('suratPermohonan'),
                  },
                ]"
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.png"
                  hidden
                  @change="handlePermohonanUpload"
                  :disabled="disabledAction"
                />

                <img
                  src="~@/assets/icon/doc-grey.svg"
                  alt="upload"
                  class="upload-icon"
                />

                <div class="upload-title">Tambah file lainnya</div>

                <div class="upload-subtitle">
                  PDF, JPG, PNG (Seluruh file max. 10MB)
                </div>
              </label>
              <p
                class="error-text"
                v-if="fieldErrors.includes('suratPermohonan')"
              >
                Silahkan pilih file untuk melanjutkan.
              </p>
            </div>
            <hr />
          </component-guard>

          <!-- SURAT PERSETUJUAN -->
          <component-guard :requiredPermissions="['30.2']">
            <div class="document-section">
              <h6 class="section-title">Surat Persetujuan</h6>

              <p class="section-description">
                Upload surat persetujuan yang sudah ditandatangani.
              </p>

              <div
                v-for="(file, index) in suratPersetujuanFiles"
                :key="'persetujuan-' + index"
                class="uploaded-file"
              >
                <div>
                  <i class="far fa-check-circle mr-2"></i>
                  {{ file.name }}
                </div>

                <div @click="removePersetujuan(index)">
                  <i class="fas fa-times remove-file"></i>
                </div>
              </div>

              <label
                class="upload-box"
                :class="[
                  {
                    'upload-box-error':
                      fieldErrors.includes('suratPersetujuan'),
                  },
                ]"
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.png"
                  hidden
                  @change="handlePersetujuanUpload"
                  :disabled="disabledAction"
                />

                <img
                  src="~@/assets/icon/doc-grey.svg"
                  alt="upload"
                  class="upload-icon"
                />

                <div class="upload-title">Tambah file lainnya</div>

                <div class="upload-subtitle">
                  PDF, JPG, PNG (Seluruh file max. 10MB)
                </div>
              </label>
              <p
                class="error-text"
                v-if="fieldErrors.includes('suratPersetujuan')"
              >
                Silahkan pilih file untuk melanjutkan.
              </p>
            </div>
            <hr />
          </component-guard>

          <!-- INFO BOX -->
          <div class="alert alert-info custom-alert">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="#1D54B0" stroke-width="1.5" />
              <text
                x="8"
                y="11"
                text-anchor="middle"
                font-size="10"
                fill="#1D54B0"
              >
                !
              </text>
            </svg>
            <span class="ml-2"
              >Surat Permohonan dari Klien dan Surat Persetujuan memungkinkan
              penguploadan beberapa dokumen secara bersamaan.</span
            >
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button
            @click="closeModal"
            class="btn btn-outline-secondary"
            data-dismiss="modal"
          >
            Batal
          </button>

          <button
            v-if="
              suratPermohonanFiles.length > 0 ||
              suratPersetujuanFiles.length > 0
            "
            class="btn btn-danger"
            @click="resetFiles"
            :disabled="isUploading || disabledAction"
          >
            Reset
          </button>

          <button
            class="btn btn-primary"
            @click="uploadDocuments"
            :disabled="isUploading || disabledAction"
          >
            <span
              v-if="isUploading"
              class="spinner-border spinner-border-sm mr-2"
            ></span>

            Upload {{ totalFiles === 0 ? "" : totalFiles }} Dokumen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import { alert } from "@/utils";
import { ComponentGuard, permissionMixin } from "@/components/Permissions";

export default {
  name: "UploadApplicationDocument",
  components: {
    ComponentGuard,
  },
  mixins: [permissionMixin],
  data() {
    return {
      suratPermohonanFiles: [],
      suratPersetujuanFiles: [],
      fieldErrors: [],
      isUploading: false,
    };
  },

  computed: {
    ...mapGetters("rentAdjustment", {
      transferUnitId: "getId",
      transferUnitStatus: "getStatus",
    }),
    totalFiles() {
      return (
        this.suratPermohonanFiles.length + this.suratPersetujuanFiles.length
      );
    },
    disabledAction() {
      return this.transferUnitStatus !== "verification";
    },
    hasEditSuratPermohonanPermission() {
      return this.hasPermission({ requiredPermissions: "29.2" });
    },
    hasEditSuratPersetujuanPermission() {
      return this.hasPermission({ requiredPermissions: "30.2" });
    },
  },

  methods: {
    ...mapActions("rentAdjustment", [
      "uploadApplicationDocument",
      "getVerificationStatus",
      "getDetailTransferLessee",
    ]),
    closeModal() {
      this.resetFiles();
      this.fieldErrors = [];
    },
    validateFile(file) {
      // const maxSize = 10 * 1024 * 1024;

      // allowed format is PDF, JPG, and PNG
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

      if (!allowedTypes.includes(file.type)) {
        alert().fire({
          icon: "error",
          title: "Format file tidak diizinkan.",
          text: "Silahkan pilih file dengan format PDF, JPG, atau PNG.",
        });

        return false;
      }

      // if (file.size > maxSize) {
      //   alert(`File ${file.name} melebihi 10MB`);
      //   return false;
      // }

      return true;
    },

    handlePermohonanUpload(event) {
      let files = Array.from(event.target.files);
      files = files.concat(this.suratPermohonanFiles);

      const totalSize = files.reduce((acc, file) => acc + file.size, 0);
      const maxTotalSize = 10 * 1024 * 1024; // 10MB

      if (totalSize > maxTotalSize) {
        alert().fire({
          icon: "error",
          title: "Ukuran file melebihi batas.",
          text: "Total ukuran seluruh file tidak boleh melebihi 10MB.",
        });
        return;
      }

      this.suratPermohonanFiles = [];
      files.forEach((file) => {
        if (this.validateFile(file)) {
          this.suratPermohonanFiles.push(file);
        }
      });

      event.target.value = "";
      this.fieldErrors = this.fieldErrors.filter(
        (error) => error !== "suratPermohonan"
      );
    },

    handlePersetujuanUpload(event) {
      let files = Array.from(event.target.files);
      files = files.concat(this.suratPersetujuanFiles);

      const totalSize = files.reduce((acc, file) => acc + file.size, 0);
      const maxTotalSize = 10 * 1024 * 1024; // 10MB

      if (totalSize > maxTotalSize) {
        alert().fire({
          icon: "error",
          title: "Ukuran file melebihi batas.",
          text: "Total ukuran seluruh file tidak boleh melebihi 10MB.",
        });
        return;
      }

      this.suratPersetujuanFiles = [];
      files.forEach((file) => {
        if (this.validateFile(file)) {
          this.suratPersetujuanFiles.push(file);
        }
      });

      event.target.value = "";
      this.fieldErrors = this.fieldErrors.filter(
        (error) => error !== "suratPersetujuan"
      );
    },

    removePermohonan(index) {
      this.suratPermohonanFiles = this.suratPermohonanFiles.filter(
        (_, i) => i !== index
      );
    },

    removePersetujuan(index) {
      this.suratPersetujuanFiles = this.suratPersetujuanFiles.filter(
        (_, i) => i !== index
      );
    },

    resetFiles() {
      this.suratPermohonanFiles = [];
      this.suratPersetujuanFiles = [];
    },

    async uploadDocuments() {
      this.fieldErrors = [];
      if (
        this.hasEditSuratPermohonanPermission &&
        this.suratPermohonanFiles.length === 0
      ) {
        this.fieldErrors.push("suratPermohonan");
      }
      // if (this.suratPermohonanFiles.length === 0) {
      //   this.fieldErrors.push("suratPermohonan");
      // }

      if (
        this.hasEditSuratPersetujuanPermission &&
        this.suratPersetujuanFiles.length === 0
      ) {
        this.fieldErrors.push("suratPersetujuan");
      }

      if (this.fieldErrors.length > 0) {
        return;
      }

      const sentData = {
        id: this.transferUnitId,
        d01: [],
        d02: [],
      };
      const suratPermohonanPromises = this.suratPermohonanFiles.map(
        async (file) => {
          const base64Data = await this.fileToBase64(file);
          return {
            fileName: file.name,
            fileType: file.type,
            fileData: base64Data,
          };
        }
      );

      const suratPersetujuanPromises = this.suratPersetujuanFiles.map(
        async (file) => {
          const base64Data = await this.fileToBase64(file);
          return {
            fileName: file.name,
            fileType: file.type,
            fileData: base64Data,
          };
        }
      );

      const suratPermohonanData = await Promise.all(suratPermohonanPromises);
      const suratPersetujuanData = await Promise.all(suratPersetujuanPromises);

      sentData.d01 = suratPermohonanData;
      sentData.d02 = suratPersetujuanData;

      try {
        const response = await this.uploadApplicationDocument(sentData);
        if (!response.isSuccess) {
          return alert().fire({
            icon: "error",
            title: "Gagal mengupload dokumen",
            text:
              response.message || "Terjadi kesalahan saat mengupload dokumen.",
          });
        }

        let modalDesc = "Dokumen permohonan dan persetujuan berhasil diupload.";
        if (sentData.d01.length > 0 && sentData.d02.length > 0) {
          modalDesc = "Dokumen permohonan dan persetujuan berhasil diupload.";
        } else if (sentData.d01.length > 0 && sentData.d02.length === 0) {
          modalDesc = "Dokumen permohonan berhasil diupload.";
        } else if (sentData.d01.length === 0 && sentData.d02.length > 0) {
          modalDesc = "Dokumen persetujuan berhasil diupload.";
        }
        await alert().fire({
          icon: "success",
          title: "Dokumen berhasil diupload",
          text: modalDesc,
        });
        this.resetFiles();
        this.getVerificationStatus({
          transferUnitId: this.transferUnitId,
          type: "document",
        });
        this.getDetailTransferLessee(this.transferUnitId);
        $("#uploadDocumentModal").modal("hide");
      } catch (error) {
        return alert().fire({
          icon: "error",
          title: "Gagal mengupload dokumen",
          text: "Terjadi kesalahan saat mengupload dokumen.",
        });
      }
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = (error) => reject(error);

        reader.readAsDataURL(file);
      });
    },
  },
};
</script>
<style scoped>
.modal-content {
  border-radius: 16px;
  border: none;
}

.modal-header {
  padding: 24px;
}

.modal-body {
  padding: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
}

.section-description {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 12px;
}

.uploaded-file {
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 0 12px;

  border: 1px solid #a3e19f;
  background: #f4fff3;
  border-radius: 4px;

  margin-bottom: 8px;

  font-size: 14px;
}

.remove-file {
  color: #ff4d4f;
  cursor: pointer;
}

.upload-box {
  width: 100%;
  min-height: 110px;

  border: 1px dashed #cfd8e3;
  border-radius: 6px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  cursor: pointer;

  margin-top: 8px;
}

.upload-box:hover {
  background: #fafcff;
}

.upload-icon {
  font-size: 28px;
  color: #9ca3af;
  margin-bottom: 8px;
}

.upload-title {
  font-size: 14px;
  color: #374151;
}

.upload-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
}

.custom-alert {
  margin-bottom: 0;
  font-size: 14px;
  color: #1d54b0 !important;
  border: 1px solid #92d4fe !important;
  background-color: #eff8ff !important;
  border-radius: 8px !important;
  display: flex;
  align-items: start;
}

.modal-footer {
  padding: 16px 24px;
}

.upload-box-error {
  border-color: #ff3444 !important;
}

.error-text {
  color: #ff3444;
  font-size: 13px;
}

.btn {
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
}

.btn:disabled {
  background-color: #d1d5dc;
  cursor: not-allowed;
  color: #ffffff;
  border: none;
}
</style>


// WEBPACK FOOTER //
// src/components/RentAdjustment/CustomComponent/UploadApplicationDocument.vue